import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, CheckCircle2, FileText, Play, Receipt, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { platformApi } from '../api'
import { Modal } from '../components/Modal'
import { StatusPill, invoiceTone } from '../components/StatusPill'
import type { BillingRunResult, InvoiceView } from '../types'

function money(minor: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 100)
}

function thisMonth() {
  return new Date().toISOString().slice(0, 7)
}

/** The monthly run, and every invoice it has produced. */
export function PlatformBillingPage() {
  const queryClient = useQueryClient()

  const [period, setPeriod] = useState(thisMonth())
  const [vatPercent, setVatPercent] = useState(5)
  const [dueInDays, setDueInDays] = useState(14)
  const [preview, setPreview] = useState<BillingRunResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [payFor, setPayFor] = useState<InvoiceView | null>(null)
  const [voidFor, setVoidFor] = useState<InvoiceView | null>(null)

  const invoices = useQuery({
    queryKey: ['platform', 'invoices'],
    queryFn: () => platformApi.invoices(),
  })

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'invoices'] })
  }

  function onError(err: unknown) {
    const detail = (err as { response?: { data?: { detail?: string; title?: string } } }).response
      ?.data
    setError(detail?.detail ?? detail?.title ?? 'That did not work.')
  }

  const run = useMutation({
    mutationFn: (commit: boolean) =>
      platformApi.runBilling(period, vatPercent, dueInDays, commit),
    onSuccess: (result) => {
      setPreview(result)
      setError(null)
      if (result.committed) refresh()
    },
    onError,
  })

  const issue = useMutation({
    mutationFn: (id: string) => platformApi.issueInvoice(id),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError,
  })

  const markPaid = useMutation({
    mutationFn: ({ id, paidOn, reference }: { id: string; paidOn: string; reference: string }) =>
      platformApi.markInvoicePaid(id, paidOn, reference),
    onSuccess: () => {
      setPayFor(null)
      setError(null)
      refresh()
    },
    onError,
  })

  const voidInvoice = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      platformApi.voidInvoice(id, reason),
    onSuccess: () => {
      setVoidFor(null)
      setError(null)
      refresh()
    },
    onError,
  })

  const rows = invoices.data ?? []
  const outstanding = rows
    .filter((row) => row.status === 'ISSUED')
    .reduce((sum, row) => sum + row.totalMinor, 0)
  const currency = rows[0]?.currency ?? 'AED'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-heading)]">Billing</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Run a month, then issue what it produced.
        </p>
      </div>

      <dl className="grid gap-4 sm:grid-cols-3">
        <Tile label="Invoices" value={rows.length.toLocaleString()} />
        <Tile label="Issued, unpaid" value={money(outstanding, currency)} caption="awaiting payment" />
        <Tile
          label="Paid"
          value={rows.filter((row) => row.status === 'PAID').length.toLocaleString()}
        />
      </dl>

      {error && (
        <p
          role="alert"
          className="rounded-[var(--radius-card)] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        >
          {error}
        </p>
      )}

      {/* ---------------------------------------------------------- the run */}
      <section className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
        <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-heading)]">
          <Play className="size-4" aria-hidden />
          Monthly run
        </h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Preview first. The dry run makes the same selection and the same arithmetic and writes
          nothing.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label htmlFor="billing-period">Period</Label>
            <Input
              id="billing-period"
              type="month"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              className="w-44"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-vat">VAT %</Label>
            <Input
              id="billing-vat"
              type="number"
              min={0}
              max={100}
              value={vatPercent}
              onChange={(event) => setVatPercent(Number(event.target.value))}
              className="w-24"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-due">Due in days</Label>
            <Input
              id="billing-due"
              type="number"
              min={0}
              value={dueInDays}
              onChange={(event) => setDueInDays(Number(event.target.value))}
              className="w-28"
            />
          </div>
          <Button variant="secondary" disabled={run.isPending} onClick={() => run.mutate(false)}>
            Preview
          </Button>
          <Button
            disabled={run.isPending || !preview || preview.committed}
            onClick={() => run.mutate(true)}
            title={preview ? undefined : 'Preview the run first'}
          >
            <Receipt className="me-2 size-4" aria-hidden />
            Generate invoices
          </Button>
        </div>

        {preview && (
          <div className="mt-5 space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <StatusPill tone={preview.committed ? 'good' : 'info'}>
                {preview.committed ? 'Generated' : 'Preview'}
              </StatusPill>
              <span className="text-[var(--color-muted)]">
                {preview.invoiced} to invoice · {preview.skipped} skipped
              </span>
              {Object.entries(preview.totalsByCurrency).map(([code, total]) => (
                <span key={code} className="font-medium tabular-nums text-[var(--color-heading)]">
                  {money(total, code)}
                </span>
              ))}
            </div>

            <div className="overflow-x-auto rounded-md border border-[var(--color-border)]">
              <table className="w-full min-w-[38rem] text-sm">
                <thead className="bg-[var(--color-page)] text-xs uppercase text-[var(--color-muted)]">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">Workspace</th>
                    <th className="px-3 py-2 text-start font-medium">Outcome</th>
                    <th className="px-3 py-2 text-end font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line) => (
                    <tr key={line.tenantId} className="border-t border-[var(--color-border)]">
                      <td className="px-3 py-2">{line.tenantName}</td>
                      <td className="px-3 py-2">
                        {line.outcome === 'SKIPPED' ? (
                          <span className="text-[var(--color-muted)]">{line.detail}</span>
                        ) : (
                          <span>{line.planCode}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {line.outcome === 'SKIPPED'
                          ? '—'
                          : money(line.totalMinor, line.currency ?? currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* ------------------------------------------------------- invoices */}
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
        <table className="w-full min-w-[52rem] text-sm">
          <thead className="bg-[var(--color-page)] text-xs uppercase text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3 text-start font-medium">Invoice</th>
              <th className="px-4 py-3 text-start font-medium">Workspace</th>
              <th className="px-4 py-3 text-start font-medium">Period</th>
              <th className="px-4 py-3 text-start font-medium">Status</th>
              <th className="px-4 py-3 text-end font-medium">Total</th>
              <th className="px-4 py-3 text-end font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.isPending && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[var(--color-muted)]">
                  Loading…
                </td>
              </tr>
            )}

            {!invoices.isPending && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <FileText className="mx-auto mb-2 size-6 text-[var(--color-muted)]" aria-hidden />
                  <p className="text-sm text-[var(--color-muted)]">
                    No invoices yet. Run a month above.
                  </p>
                </td>
              </tr>
            )}

            {rows.map((invoice) => (
              <tr key={invoice.id} className="border-t border-[var(--color-border)]">
                <td className="px-4 py-3 font-medium text-[var(--color-heading)]">
                  {invoice.number}
                </td>
                <td className="px-4 py-3">{invoice.tenantName ?? '—'}</td>
                <td className="px-4 py-3 text-[var(--color-muted)]">
                  {invoice.periodStart} → {invoice.periodEnd}
                </td>
                <td className="px-4 py-3">
                  <StatusPill tone={invoiceTone(invoice.status)}>
                    {invoice.status.toLowerCase()}
                  </StatusPill>
                  {invoice.status === 'ISSUED' && invoice.dueOn && (
                    <div className="mt-1 text-xs text-[var(--color-muted)]">
                      due {invoice.dueOn}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-end tabular-nums">
                  {money(invoice.totalMinor, invoice.currency)}
                  {invoice.taxMinor > 0 && (
                    <div className="text-xs text-[var(--color-muted)]">
                      incl. {money(invoice.taxMinor, invoice.currency)} tax
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {invoice.status === 'DRAFT' && (
                      <Button size="sm" variant="ghost" onClick={() => issue.mutate(invoice.id)}>
                        <Send className="me-1.5 size-4" aria-hidden />
                        Issue
                      </Button>
                    )}
                    {invoice.status === 'ISSUED' && (
                      <Button size="sm" variant="ghost" onClick={() => setPayFor(invoice)}>
                        <CheckCircle2 className="me-1.5 size-4" aria-hidden />
                        Mark paid
                      </Button>
                    )}
                    {invoice.status !== 'PAID' && invoice.status !== 'VOID' && (
                      <Button size="sm" variant="ghost" onClick={() => setVoidFor(invoice)}>
                        <Ban className="me-1.5 size-4" aria-hidden />
                        Void
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MarkPaidModal
        invoice={payFor}
        pending={markPaid.isPending}
        onClose={() => setPayFor(null)}
        onSubmit={(paidOn, reference) =>
          payFor && markPaid.mutate({ id: payFor.id, paidOn, reference })
        }
      />

      <VoidModal
        invoice={voidFor}
        pending={voidInvoice.isPending}
        onClose={() => setVoidFor(null)}
        onSubmit={(reason) => voidFor && voidInvoice.mutate({ id: voidFor.id, reason })}
      />
    </div>
  )
}

function MarkPaidModal({
  invoice,
  pending,
  onClose,
  onSubmit,
}: {
  invoice: InvoiceView | null
  pending: boolean
  onClose: () => void
  onSubmit: (paidOn: string, reference: string) => void
}) {
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10))
  const [reference, setReference] = useState('')

  return (
    <Modal
      open={invoice !== null}
      title={`Record payment for ${invoice?.number ?? ''}`}
      description={
        invoice ? `${money(invoice.totalMinor, invoice.currency)} from ${invoice.tenantName}` : ''
      }
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={() => onSubmit(paidOn, reference)}>
            {pending ? 'Saving…' : 'Mark paid'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="paid-on">Paid on</Label>
        <Input
          id="paid-on"
          type="date"
          value={paidOn}
          onChange={(event) => setPaidOn(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="paid-reference">Payment reference</Label>
        <Input
          id="paid-reference"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          placeholder="Bank transfer reference"
        />
      </div>
    </Modal>
  )
}

function VoidModal({
  invoice,
  pending,
  onClose,
  onSubmit,
}: {
  invoice: InvoiceView | null
  pending: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
}) {
  const [reason, setReason] = useState('')

  return (
    <Modal
      open={invoice !== null}
      title={`Void ${invoice?.number ?? ''}`}
      description="The invoice stays on the record as voided. A paid one needs a credit note instead."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={pending || reason.trim().length === 0}
            onClick={() => onSubmit(reason.trim())}
          >
            {pending ? 'Voiding…' : 'Void invoice'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="void-reason">Reason</Label>
        <Input
          id="void-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why this invoice should not stand"
        />
      </div>
    </Modal>
  )
}

function Tile({ label, value, caption }: { label: string; value: string; caption?: string }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <dt className="text-xs uppercase text-[var(--color-muted)]">{label}</dt>
      <dd className="mt-2 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
        {value}
      </dd>
      {caption && <p className="text-xs text-[var(--color-muted)]">{caption}</p>}
    </div>
  )
}
